import { useAuth } from '@agentnet/auth';
import { useIsFeatureEnabled } from '@agentnet/feature-flags';
import { CellLink, CellMessage, CellStatus, Content, DataTable, pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, ButtonGroup, Divider, FormControlLabel, Grid, Switch, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ArrowBack } from '@material-ui/icons';
import {
  CellClickedEvent,
  CheckboxSelectionCallbackParams,
  ColDef,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  ITooltipParams,
  RowNode,
  SortChangedEvent,
  ValueFormatterParams,
} from 'ag-grid-enterprise';
import { AgGridReactProps } from 'ag-grid-react';
import {
  bookOrderApi,
  dequeueFilesApi,
  enqueueFilesApi,
  getAccountInfoApi,
  getFilesApi,
  getOrderFileDetailsApi,
} from 'api/orderManagement/order-management-api';
import clsx from 'clsx';
import { FileEditAssignmentContainer } from 'core/layout-core/core-content/FileContent/FileEditAssignmentContainer';
import ReportsAndPayDrawer from 'core/layout-core/core-content/ReportsAndPayDrawer';
import { AsyncStatus } from 'features/files/fincen-reporting/constants';
import FloatingFilterComponent from 'features/files/files-summary/files-list/FloatingFilterComponent';
import CalculatorRenderer from 'features/Remittance/CalculatorRenderer';
import { filterParams } from 'features/Remittance/reportsListConfig';
import useRatesAndFee from 'features/Remittance/useRatesAndFee';
import { useOrderInfo } from 'hooks/OrderInfoContext';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import useAsync from 'hooks/useAsync';
import useSessionStorage from 'hooks/useSessionStorage';
import moment from 'moment';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import { AgentNetConfirmationDialog } from 'ui-kit/components/modal/ConfirmationDialog';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import { dateTooltipValue, getFullName } from 'utilities/utilities';
import { BookingErrorReasons } from './BookingErrorReasons';
import { ConversationDrawer } from './ConversationDrawer';
import { CreateNewFileDrawer } from './CreateNewFileDrawer';
import FileStatusIndicator from './FileStatusIndicator';
import {
  ActivityRights,
  FileInfoStatus,
  getColorsByFileInfoStatus,
  getColorsByProcessingStatus,
  getKeyByValue,
  getRestrictedFiles,
  isFileQueueable,
  ProcessingStatus,
  QueueFilter,
  TeamAssignment,
} from './enum';
import { fileListFilterParams, formatDate, numberValueFormatter, queueFilters } from './fileListConfig';
import {
  getOrderAccountNumbers,
  getQueueGuardUpdate,
  hasSystemCancelledAccount,
  isBookOrderDisabled,
  SYSTEM_CANCELLED_BOOK_TOOLTIP,
} from './orderAccountInfo';
import { PolicyNumberSearchField } from './PolicyNumberSearchField';
import {
  AccountInfoRequestType,
  AccountInfoType,
  BookedOrderType,
  ClosedFileDetailType,
  CreateFileResponseModel,
  FileCountsType,
  FileEditAssignmentType,
  FileType,
  GetNotesActionLog,
  OrderFileDetailsType,
  OrderFileQueueResponseType,
  OrderFilesRequestType,
  OrderType,
} from './types';

const colorsByStatus = getColorsByProcessingStatus();
const fileInfoStatusColors = getColorsByFileInfoStatus();

const SelectFiles = () => {
  const { getAccessToken } = useAuth();

  const history = useHistory();
  const gridApiRef = useRef<GridApi | null>(null);
  const { addGlobalMsg } = useGlobalMessages();

  const hideCreateNewFileButton = useIsFeatureEnabled(FeatureFlag.ALLOW_OM_CREATE_NEW_FILE);
  const location = useLocation<{ order: OrderType; from?: string }>();
  const orderData = location.state?.order;

  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { profile, userFirm } = profileCtx;
  const orderId: number = orderData?.orderId || 0;
  const targetTab: string = orderData?.targetTab || QueueFilter.AllFiles;

  const [queue, setQueue] = useState<Array<FileType>>([]);
  const [, setSessionData] = useSessionStorage('bookOrderData');
  const [allFiles, setAllFiles] = useState<Array<FileType>>([]);
  const [queueFilter, setQueueFilter] = useState<string>(targetTab);
  const [selectedFiles, setSelectedFiles] = useState<Array<FileType>>([]);
  const [selectedQueueFiles, setSelectedQueueFiles] = useState<Array<FileType>>([]);
  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
  const [openFileConflictModal, setOpenFileConflictModal] = useState<boolean>(false);
  const [openBookOrderErrorModal, setOpenBookOrderErrorModal] = useState<boolean>(false);
  const [fileConflictOrderIds, setFileConflictOrderIds] = useState<number[]>([]);
  const pendingConflictFileIdsRef = useRef<string[]>([]);
  const [displayedRowCount, setDisplayedRowCount] = useState<number>(0);
  const [searchPolicyNumbers, setSearchPolicyNumbers] = useState<string>('');
  const [editAssignmentDrawerOpen, setEditAssignmentDrawerOpen] = useState<boolean>(false);
  const [editAssignmentData, setEditAssignmentData] = useState<Array<FileEditAssignmentType>>([]);
  const [createNewFileDrawerOpen, setCreateNewFileDrawerOpen] = useState<boolean>(false);
  const [closedFiles, setClosedFiles] = useState<Array<ClosedFileDetailType>>([]);
  const [showClosedFiles, setShowClosedFiles] = useState<boolean>(false);
  const [isPartialBook, setIsPartialBook] = useState<boolean>(false);
  const [newFileCreated, setNewFileCreated] = useState<CreateFileResponseModel | null>(null);

  const allFilesTabSelected = useCallback(() => queueFilter === QueueFilter.AllFiles, [queueFilter]);
  const orderQueueTabSelected = useCallback(() => queueFilter === QueueFilter.OrderQueue, [queueFilter]);

  const getSelectedRows = (): FileType[] => gridApiRef.current?.getSelectedRows() ?? [];
  const selectedRows = getSelectedRows();

  const setSelectedRowsForTab = (selectedRows: FileType[]) => {
    if (allFilesTabSelected()) {
      setSelectedFiles(selectedRows);
    } else if (orderQueueTabSelected()) {
      setSelectedQueueFiles(selectedRows);
    }
  };

  const { drawerData, setDrawerData, openDrawer, setOpenDrawer, handleDrawerOpen, rateAndFeeExecute } = useRatesAndFee(
    userFirm?.firmId ?? '',
    'order-management',
  );

  const calculateGrossTotal = (rows: FileType[]): number => {
    return parseFloat(rows.reduce((acc, row) => acc + row.gross, 0).toFixed(2));
  };

  const calculateNetTotal = (rows: FileType[]): number => {
    return parseFloat(rows.reduce((acc, row) => acc + row.net, 0).toFixed(2));
  };

  const calculateNetDifference = (netTotal: number, reportedAmount: number): number => {
    return parseFloat((netTotal - reportedAmount).toFixed(2));
  };

  const dataQaPrefix = 'OrderManagement-SelectFiles';

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        width: '100%',
        marginTop: pxToRem(114),
      },
      contentWrap: {
        justifyContent: 'space-between',
        display: 'flex',
        flexDirection: 'column',
      },
      backButton: {
        margin: theme.spacing(2, 0, -3, 3),
      },
      pageHeaderTitle: {
        textTransform: 'uppercase',
      },
      tableDefault: {
        height: `calc(${orderQueueTabSelected() && closedFiles?.length ? '85vh' : '90vh'} - ${
          selectedRows?.length ? '367px' : '307px'
        }) !important`,
      },
      paddingX: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        width: 'auto',
      },
      selectedRows: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100% !important',
        fontSize: pxToRem(14),
        padding: theme.spacing(3),
        paddingBottom: pxToRem(12),
      },
      selectedRowsActive: {
        marginBottom: '59px',
        paddingBottom: 0,
      },
      noRows: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 220,
        justifyContent: 'center',
      },
      closedFilesContainer: {
        paddingTop: 0,
        paddingLeft: pxToRem(35),
        '& .ag-center-cols-container, .ag-center-cols-viewport': {
          minHeight: theme.spacing(5),
        },
      },
      switch: {
        marginLeft: 0,
        justifyContent: 'left',
        '& span': {
          fontWeight: 'bold',
        },
      },
      orderDetails: {
        width: '100%',
        marginBottom: 'auto',
        marginLeft: theme.spacing(1),
        paddingBottom: theme.spacing(5),
      },
      orderNumber: {
        fontSize: pxToRem(17),
        marginBottom: pxToRem(20),
      },
      fileCount: {
        marginTop: pxToRem(12),
      },
      footerWrap: {
        position: 'fixed',
        bottom: 0,
        background: '#E7F1FA',
        borderTop: `1px solid`,
        borderColor: theme.palette.divider,
        width: '100%',
        padding: '11px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        zIndex: 11,
        left: 0,
      },
      footerActions: {
        '& .MuiButton-root': {
          marginLeft: theme.spacing(2),
        },
      },
      cancelBtn: {
        backgroundColor: theme.palette.brand.feather50,
      },
      createNewFileBtn: {
        marginTop: theme.spacing(3),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'right',
      },
      orderStatusLabel: {
        marginLeft: theme.spacing(2),
        padding: theme.spacing(0.5, 1.5),
        borderRadius: theme.shape.borderRadius,
        fontSize: pxToRem(14),
        fontWeight: 'bold',
        display: 'inline-flex',
        alignItems: 'center',
      },
      openStatus: {
        backgroundColor: '#e6f4ea',
        color: '#137333',
      },
      cancelledStatus: {
        backgroundColor: '#fce8e6',
        color: '#c5221f',
      },
      orderNotesLabel: {
        padding: theme.spacing(0, 0, 0, 1),
      },
      orderNotesIcon: {
        height: pxToRem(20),
        '& .MuiIconButton-root': {
          padding: 0,
        },
      },
    }),
  );

  const classes = useStyles();

  const getFiles = async (firmId: number, fileInfoStatus?: number): Promise<Array<FileType>> => {
    const token = await getAccessToken();
    const response = await getFilesApi(firmId, token, fileInfoStatus?.toString());
    return response.result;
  };

  const getFileListData = async (): Promise<FileType[]> => {
    const firmId = Number(userFirm?.firmId) || 0;
    const files = await getFiles(firmId);
    // Resolve the status label from its id when the API omits it, rather than assuming In Process
    for (const file of files) {
      file.fileStatus ||= getKeyByValue(ProcessingStatus, file.fileStatusId) ?? '';
      file.status = file.fileStatus;
      file.policyNumbersSearch = file.policyNumbers?.join(',');
      if (file.fileInfoStatusId && !file.fileInfoStatus) {
        file.fileInfoStatus = getKeyByValue(FileInfoStatus, file.fileInfoStatusId) || undefined;
      }
    }
    setAllFiles(files);
    allFilesTabSelected() && setDisplayedRowCount(files?.length);
    return files;
  };

  const {
    execute: executeFileList,
    status: executeFileListStatus,
    value: fileListResults,
    errors: fileListErrors,
  } = useAsync<FileType[]>(getFileListData, false);

  const getOrderFilesDetails = async (): Promise<OrderFileDetailsType> => {
    const token = await getAccessToken();
    const result = await getOrderFileDetailsApi(orderId, token);
    result?.openFiles.forEach((file) => {
      file.fileStatus ||= getKeyByValue(ProcessingStatus, file.fileStatusId) ?? '';
      file.status = file.fileStatus;
      file.policyNumbersSearch = file.policyNumbers?.join(',');
      // Convert fileInfoStatusId to fileInfoStatus string if needed
      if (file.fileInfoStatusId && !file.fileInfoStatus) {
        file.fileInfoStatus = getKeyByValue(FileInfoStatus, file.fileInfoStatusId) || undefined;
      }
    });
    // Also process closed files
    result?.closedFiles.forEach((closedFileDetail) => {
      closedFileDetail.files.forEach((file) => {
        file.fileStatus ||= getKeyByValue(ProcessingStatus, file.fileStatusId) ?? '';
        file.status = file.fileStatus;
        file.policyNumbersSearch = file.policyNumbers?.join(',');
        // Convert fileInfoStatusId to fileInfoStatus string if needed
        if (file.fileInfoStatusId && !file.fileInfoStatus) {
          file.fileInfoStatus = getKeyByValue(FileInfoStatus, file.fileInfoStatusId) || undefined;
        }
      });
    });
    orderQueueTabSelected() && setDisplayedRowCount(result?.openFiles?.length);
    return result;
  };

  const {
    execute: executeGetOrderFilesDetails,
    status: executeGetOrderFilesDetailsStatus,
    value: getOrderFilesDetailsValue,
    errors: executeGetOrderFilesDetailsErrors,
  } = useAsync<OrderFileDetailsType>(getOrderFilesDetails, false);

  const refreshFilesInTabAfterDrawerClose = () => {
    if (allFilesTabSelected()) {
      executeFileList();
    } else if (orderQueueTabSelected()) {
      executeGetOrderFilesDetails();
    }
  };

  const enqueueFiles = async (fileIds: string[], overrideConflict = false): Promise<OrderFileQueueResponseType> => {
    const token = await getAccessToken();
    const payload: OrderFilesRequestType = {
      orderId,
      fileIds,
      fileStatusId: ProcessingStatus.New,
      fscRoleId: TeamAssignment.IFSC,
      overrideConflict,
    };
    const response = await enqueueFilesApi(orderId, payload, token);
    return response;
  };

  const {
    execute: executeEnqueueFiles,
    status: enqueueFilesStatus,
    errors: enqueueFilesErrors,
    value: enqueueFilesValue,
  } = useAsync<OrderFileQueueResponseType>(enqueueFiles, false);

  const dequeueFiles = async (fileIds: string[]): Promise<OrderFileQueueResponseType> => {
    const token = await getAccessToken();
    const payload: OrderFilesRequestType = {
      orderId,
      fileIds,
      fileStatusId: ProcessingStatus['Upload Review'],
    };
    const response = await dequeueFilesApi(orderId, payload, token);
    return response;
  };

  const {
    execute: executeDequeueFiles,
    status: dequeueFilesStatus,
    errors: dequeueFilesErrors,
  } = useAsync<OrderFileQueueResponseType>(dequeueFiles, false);

  const disableSecondaryButton = enqueueFilesStatus === 'pending' || dequeueFilesStatus === 'pending';

  const getAccountInfo = async (accountNumbers: number[]): Promise<AccountInfoType[]> => {
    if (accountNumbers.length === 0) {
      return [];
    }
    const token = await getAccessToken();
    const accountInfoNumbers: AccountInfoRequestType = { accountNumbers: accountNumbers };
    return await getAccountInfoApi(accountInfoNumbers, token);
  };

  const { execute: getAccountInfoApiExecute, value: accountInfoApiResult } = useAsync<AccountInfoType[], [number[]]>(
    getAccountInfo,
    false,
  );
  const {
    execute: getAllOrderAccountInfoApiExecute,
    status: allOrderAccountInfoApiStatus,
    value: allOrderAccountInfoApiResult,
  } = useAsync<AccountInfoType[], [number[]]>(getAccountInfo, false);

  const getDisplayedRowCount = () => {
    if (gridApiRef.current) return gridApiRef.current.getDisplayedRowCount();
    return 0;
  };

  const { setSelectedFilesAmount, setAccountInfoList, lastCancelledOrderId } = useOrderInfo();

  const onSelectionChanged = useCallback(() => {
    let selectedRows = getSelectedRows();
    setSelectedRowsForTab(selectedRows);

    // always include the files in Queue whether they have been selected or not.
    if (allFilesTabSelected()) {
      selectedRows = selectedRows.concat(queue);
    } else if (orderQueueTabSelected()) {
      selectedRows = queue.concat(selectedFiles);
    }

    const grossTotal = calculateGrossTotal(selectedRows);
    const netTotal = calculateNetTotal(selectedRows);
    const netDifference = calculateNetDifference(netTotal, orderData?.reportedAmount || 0);
    setSelectedFilesAmount({ calculatedGross: grossTotal, calculatedNet: netTotal, netDifference: netDifference });

    const selectedAccounts = { accountNumbers: selectedRows.map((row) => row.accountNumber) };
    const accountNumbersInOrder: number[] = selectedAccounts.accountNumbers;
    const truncatedAccountNumbers = extractUniqueAccountNumbers(accountNumbersInOrder);
    getAccountInfoApiExecute(truncatedAccountNumbers);
  }, [setSelectedFilesAmount, queueFilter, queue]);

  const [isDashboardMessagePanelOpen, setIsDashboardMessagePanelOpen] = useState<boolean>(false);
  const [conversationFileNumber, setConversationFileNumber] = useState<string>();
  const [conversationPayload, setConversationPayload] = useState<GetNotesActionLog | undefined>(undefined);

  useEffect(() => {
    if (lastCancelledOrderId === orderId) {
      // Refresh when order is cancelled
      executeGetOrderFilesDetails();
    }
  }, [lastCancelledOrderId]);

  useEffect(() => {
    const selectedNodes: IRowNode[] = [];
    gridApiRef.current?.forEachNode((node) => {
      if (selectedFiles.find((selectedFile) => selectedFile.fileId === node.data.fileId)) {
        selectedNodes.push(node);
      } else if (selectedQueueFiles.find((selectedFile) => selectedFile.fileId === node.data.fileId)) {
        selectedNodes.push(node);
      }
    });
    if (selectedNodes.length > 0) {
      gridApiRef.current?.setNodesSelected({ nodes: selectedNodes, newValue: true });
    }
  }, [queueFilter]);

  useEffect(() => {
    if (accountInfoApiResult) {
      let accountInfoArray: AccountInfoType[] = [];
      accountInfoArray = accountInfoApiResult.map(
        ({
          accountNumber,
          contractualSplit,
          costCenter,
          isNationalAgentFlag,
          jurisdictionStateCode,
          remitAccountNumber,
          underwriterCode,
          accountStatus,
          agentLinxStatus,
        }) => ({
          accountNumber,
          contractualSplit,
          costCenter,
          isNationalAgentFlag,
          jurisdictionStateCode,
          remitAccountNumber,
          underwriterCode,
          accountStatus,
          agentLinxStatus,
        }),
      );
      setAccountInfoList(accountInfoArray);
    }
  }, [accountInfoApiResult]);

  const extractUniqueAccountNumbers = (accountNumbers: number[]): number[] => {
    // this function returns at most 5 unique account ids from the first selected rows
    const maxItems = 5;
    const uniqueAccountNumbers: number[] = Array.from(new Set(accountNumbers));
    return uniqueAccountNumbers.slice(0, maxItems);
  };

  const netAmountTotal = selectedRows?.reduce((accumulator: number, obj: FileType) => accumulator + obj['net'], 0);
  const selectedFilesText = `Selecting ${selectedRows?.length} file(s) for ${numberValueFormatter(
    netAmountTotal,
    true,
  )}`;

  const hasFutureDate = (val: { policyDate?: string }) => {
    return val?.policyDate && +new Date(val.policyDate) > +new Date();
  };

  const onGridReady = (params: GridReadyEvent) => {
    gridApiRef.current = params.api;

    params.api.setFilterModel({
      fileInfoStatusColumn: {
        filterType: 'set',
        values: [getKeyByValue(FileInfoStatus, FileInfoStatus.Open)],
      },
    });
  };

  const onFilterChanged = (): void => {
    setDisplayedRowCount(getDisplayedRowCount());
  };

  const headerCheckboxRenderer = () => {
    return <input type="checkbox" data-testid={'OrderManagement-SelectFiles-AllCheckbox'} />;
  };

  const isFileQueueableForCurrentGrid = (file: FileType): boolean => {
    return allFilesTabSelected() ? isFileQueueable(file) : true;
  };

  const rowCheckboxRenderer = (params: { node: RowNode }) => {
    const file = params.node.data as FileType;
    const disabled = !isFileQueueableForCurrentGrid(file);

    return (
      <input
        type="checkbox"
        disabled={disabled}
        data-testid={`OrderManagement-SelectFiles-Checkbox-${params.node.id}`}
      />
    );
  };

  const policyDateRenderer = (params: { value: string }) => {
    return <div>{formatDate(params.value ?? '')}</div>;
  };

  const CustomOverlayLoading = () => {
    return (
      <div className={classes.noRows}>
        <LoadingSpinner status="pending" className="files-container--spinner-root" />
      </div>
    );
  };

  const defaultColDef: ColDef = {
    flex: 2,
    floatingFilter: true,
    filter: true,
    sortable: true,
    resizable: true,
    editable: false,
    suppressMenu: true,
    filterParams: { closeOnApply: true, suppressAndOrCondition: true },
  };

  const getCheckboxSelection = useCallback(
    (params: CheckboxSelectionCallbackParams) => {
      const file = params.data as FileType;
      return isFileQueueableForCurrentGrid(file);
    },
    [queueFilter],
  );

  const getTooltipValueGetter = useCallback(
    (params: ITooltipParams) => {
      const file = params.data as FileType;
      if (isFileQueueableForCurrentGrid(file)) {
        return '';
      }

      // Get status display text for tooltip
      const statusText =
        file.fileInfoStatus ||
        (file.fileInfoStatusId ? getKeyByValue(FileInfoStatus, file.fileInfoStatusId) : null) ||
        'restricted';

      return `Cannot queue files with ${statusText} status`;
    },
    [queueFilter],
  );

  const [columnsDefinitions, setColumnsDefinitions] = useState<ColDef[]>([
    {
      field: '',
      minWidth: 40,
      maxWidth: 40,
      filter: false,
      floatingFilter: false,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      checkboxSelection: getCheckboxSelection,
      showDisabledCheckboxes: true,
      pinned: 'left',
      headerComponent: headerCheckboxRenderer,
      cellRenderer: rowCheckboxRenderer,
      tooltipValueGetter: getTooltipValueGetter,
      resizable: false,
    },
    {
      colId: 'fileStatusIndicatorColumn',
      headerName: '',
      field: 'fileInfoStatus',
      minWidth: 50,
      maxWidth: 50,
      filter: false,
      floatingFilter: false,
      sortable: false,
      resizable: false,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <FileStatusIndicator fileInfoStatus={params.data?.fileInfoStatus} isReopened={params.data?.isReopened} />
        );
      },
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
    },
    {
      colId: 'statusColumn',
      headerName: 'Processing Status',
      field: 'status',
      minWidth: 144,
      maxWidth: 256,
      filter: 'agSetColumnFilter',
      tooltipField: 'status',
      cellRenderer: (params: ICellRendererParams) => {
        return <CellStatus status={params.value} colorsByStatus={colorsByStatus} />;
      },
    },
    {
      colId: 'fileInfoStatusColumn',
      headerName: 'File Status',
      field: 'fileInfoStatus',
      minWidth: 144,
      maxWidth: 256,
      filter: 'agSetColumnFilter',
      tooltipField: 'fileInfoStatus',
      cellRenderer: (params: ICellRendererParams) => {
        return <CellStatus status={params.value} colorsByStatus={fileInfoStatusColors} />;
      },
    },
    {
      colId: 'reasonCodeColumn',
      headerName: 'Reason Code',
      field: 'reasonCode',
      minWidth: 144,
      maxWidth: 256,
      filter: 'agSetColumnFilter',
      tooltipField: 'reasonCodeId',
    },
    {
      colId: 'fileNumberColumn',
      headerName: 'File Number',
      field: 'fileNumber',
      minWidth: 144,
      maxWidth: 384,
      filter: 'agTextColumnFilter',
      tooltipField: 'fileNumber',
      floatingFilterComponentParams: { suppressFilterButton: true },
      suppressHeaderFilterButton: true,
      cellRenderer: CellLink,
      // temporary open in new tab until we have a proper order management navigation menu
      cellRendererParams: (row: ValueFormatterParams) => ({
        linkTemplate: `/files/${row.data.fileId}/file-info`,
        openNewTab: true,
      }),
    },
    {
      colId: 'accountNumberColumn',
      headerName: 'Account Number',
      field: 'accountNumber',
      minWidth: 144,
      maxWidth: 384,
      filter: 'agTextColumnFilter',
      tooltipField: 'accountNumber',
      floatingFilterComponentParams: { suppressFilterButton: true },
      suppressHeaderFilterButton: true,
    },
    {
      colId: 'propertyAddressColumn',
      headerName: 'Property Address',
      field: 'propertyAddress',
      minWidth: 256,
      filter: 'agTextColumnFilter',
      tooltipField: 'propertyAddress',
      floatingFilterComponentParams: { suppressFilterButton: true },
      suppressHeaderFilterButton: true,
    },
    {
      colId: 'underwriterColumn',
      headerName: 'Underwriter',
      field: 'underwriter',
      minWidth: 144,
      filter: 'agSetColumnFilter',
      tooltipField: 'underwriter',
    },
    {
      colId: 'policyDateColumn',
      headerName: 'Policy Date',
      field: 'policyDate',
      minWidth: 144,
      maxWidth: 384,
      filter: 'agDateColumnFilter',
      filterParams: fileListFilterParams,
      cellRenderer: policyDateRenderer,
      sort: 'asc',
      tooltipValueGetter: (params: ITooltipParams) => {
        return hasFutureDate(params.data)
          ? 'Future dated policies cannot be Queued'
          : moment(params.value).format('MM/DD/YYYY');
      },
    },
    {
      colId: 'grossColumn',
      headerName: 'Gross',
      field: 'gross',
      minWidth: 144,
      maxWidth: 256,
      filter: 'agNumberColumnFilter',
      floatingFilterComponentParams: { suppressFilterButton: true },
      suppressHeaderFilterButton: true,
      valueFormatter: numberValueFormatter,
      tooltipValueGetter: numberValueFormatter,
      type: 'rightAligned',
      cellStyle: { 'justify-content': 'flex-end' },
    },
    {
      colId: 'netColumn',
      headerName: 'Net',
      field: 'net',
      minWidth: 144,
      maxWidth: 256,
      filter: 'agNumberColumnFilter',
      floatingFilterComponentParams: { suppressFilterButton: true },
      suppressHeaderFilterButton: true,
      valueFormatter: numberValueFormatter,
      tooltipValueGetter: numberValueFormatter,
      type: 'rightAligned',
      cellStyle: { 'justify-content': 'flex-end' },
    },
    {
      colId: 'policyNumbersSearchColumn',
      field: 'policyNumbersSearch',
      headerName: 'Policy Number',
      minWidth: 144,
      filter: 'agTextColumnFilter',
      hide: true,
    },
    {
      field: '',
      floatingFilterComponentParams: { suppressFilterButton: true },
      suppressHeaderFilterButton: true,
      floatingFilter: false,
      sortable: false,
      cellRenderer: CalculatorRenderer,
      cellRendererParams: (row: ValueFormatterParams) => ({
        tabName: 'order-management',
        isRemitValid: row.data.isRemitValid && row.data.isPricingValid,
      }),
      onCellClicked: (event: CellClickedEvent) => {
        handleDrawerOpen(event, 'order-management');
      },
      minWidth: 40,
      maxWidth: 40,
      cellStyle: { display: 'flex', justifyContent: 'center', cursor: 'pointer', alignItems: 'center' },
      tooltipValueGetter: () => 'Open Rates and Fees',
      pinned: 'right',
    },
    {
      field: '',
      floatingFilterComponentParams: { suppressFilterButton: true },
      suppressHeaderFilterButton: true,
      floatingFilter: false,
      sortable: false,
      cellRenderer: CellMessage,
      onCellClicked: (event: CellClickedEvent) => {
        try {
          const payload: GetNotesActionLog = {
            orderId: orderId,
            fileId: `${event.data.fileId}`,
          };
          setIsDashboardMessagePanelOpen(true);
          setConversationFileNumber(event.data.fileNumber);
          setConversationPayload(payload);
        } catch (e) {
          if (e instanceof Error) {
            addGlobalMsg({
              message: e.message,
              type: 'error',
            });
          }
        }
      },
      minWidth: 40,
      maxWidth: 40,
      cellStyle: { display: 'flex', justifyContent: 'center', cursor: 'pointer', alignItems: 'center' },
      tooltipValueGetter: () => 'Open conversation panel',
      pinned: 'right',
    },
  ]);

  // Update column definitions when callback functions change to fix closure capture
  useEffect(() => {
    setColumnsDefinitions((prevColumns) =>
      prevColumns.map((col) => {
        if (col.headerCheckboxSelection === true) {
          return {
            ...col,
            checkboxSelection: getCheckboxSelection,
            tooltipValueGetter: getTooltipValueGetter,
          };
        }
        return col;
      }),
    );
  }, [getCheckboxSelection, getTooltipValueGetter]);

  const closedFilesGridConfig: AgGridReactProps = {
    columnDefs: [
      {
        headerName: 'Processing Status',
        cellRenderer: (params: ICellRendererParams) => {
          return <CellStatus status={params.value} />;
        },
        field: 'fileStatus',
        filter: 'agSetColumnFilter',
        maxWidth: 256,
        tooltipField: 'fileStatus',
      },
      {
        headerName: 'File Number',
        field: 'fileNumber',
        maxWidth: 256,
        tooltipField: 'fileNumber',
        suppressHeaderFilterButton: true,
        floatingFilterComponentParams: { suppressFilterButton: true },
        cellRenderer: CellLink,
        // temporary open in new tab until we have a proper order management navigation menu
        cellRendererParams: (row: ValueFormatterParams) => ({
          linkTemplate: `/files/${row.data.fileId}/file-info`,
          openNewTab: true,
        }),
      },
      {
        headerName: 'Account Number',
        field: 'accountNumber',
        maxWidth: 256,
        tooltipField: 'accountNumber',
        suppressHeaderFilterButton: true,
        floatingFilterComponentParams: { suppressFilterButton: true },
      },
      {
        headerName: 'Property Address',
        field: 'propertyAddress',
        minWidth: 384,
        tooltipField: 'propertyAddress',
        suppressHeaderFilterButton: true,
        floatingFilterComponentParams: { suppressFilterButton: true },
      },
      {
        headerName: 'Underwriter',
        field: 'underwriter',
        filter: 'agSetColumnFilter',
        maxWidth: 256,
        tooltipField: 'underwriter',
      },
      {
        headerName: 'Policy Date',
        filter: 'agDateColumnFilter',
        filterParams: filterParams,
        field: 'policyDate',
        maxWidth: 256,
        cellRenderer: (params: ICellRendererParams) => {
          return params?.value?.length > 0 ? formatDate(params.value) : '';
        },
        tooltipValueGetter: dateTooltipValue,
      },
      {
        headerName: 'Gross',
        type: 'rightAligned',
        cellStyle: { 'justify-content': 'flex-end' },
        field: 'gross',
        filter: 'agNumberColumnFilter',
        maxWidth: 256,
        tooltipField: 'gross',
        valueFormatter: numberValueFormatter,
        suppressHeaderFilterButton: true,
        floatingFilterComponentParams: { suppressFilterButton: true },
      },
      {
        headerName: 'Net',
        type: 'rightAligned',
        cellStyle: { 'justify-content': 'flex-end' },
        field: 'net',
        filter: 'agNumberColumnFilter',
        maxWidth: 256,
        tooltipField: 'net',
        valueFormatter: numberValueFormatter,
        suppressHeaderFilterButton: true,
        floatingFilterComponentParams: { suppressFilterButton: true },
      },
      {
        field: '',
        floatingFilterComponentParams: { suppressFilterButton: true },
        suppressHeaderFilterButton: true,
        floatingFilter: false,
        sortable: false,
        cellRenderer: CalculatorRenderer,
        cellRendererParams: () => ({
          tabName: 'order-management',
          isRemitValid: true,
        }),
        onCellClicked: (event: CellClickedEvent) => {
          handleDrawerOpen(event, 'order-management');
        },
        maxWidth: 40,
        cellStyle: { display: 'flex', justifyContent: 'center', cursor: 'pointer', alignItems: 'center' },
        tooltipValueGetter: () => 'Open Rates and Fees',
        pinned: 'right',
      },
      {
        field: '',
        floatingFilterComponentParams: { suppressFilterButton: true },
        suppressHeaderFilterButton: true,
        floatingFilter: false,
        sortable: false,
        cellRenderer: CellMessage,
        onCellClicked: (event: CellClickedEvent<FileType>) => {
          try {
            const payload: GetNotesActionLog = {
              orderId: orderId,
              fileId: `${event.data?.fileId}`,
            };
            setConversationFileNumber(event.data?.fileNumber ?? '');
            setConversationPayload(payload);
          } catch (e) {
            if (e instanceof Error) {
              addGlobalMsg({
                message: e.message,
                type: 'error',
              });
            }
          }
        },
        minWidth: 40,
        maxWidth: 40,
        cellStyle: { display: 'flex', justifyContent: 'center', cursor: 'pointer', alignItems: 'center' },
        tooltipValueGetter: () => 'Open conversation panel',
        pinned: 'right',
      },
    ],
    maintainColumnOrder: true,
    gridOptions: {
      getRowId: (params: GetRowIdParams<FileType>) => String(params.data.fileId),
      suppressRowClickSelection: true,
    },
    defaultColDef: {
      flex: 1,
      floatingFilter: true,
      filter: 'agTextColumnFilter',
      sortable: true,
      resizable: true,
      editable: false,
      suppressMenu: true,
      filterParams: { closeOnApply: true, suppressAndOrCondition: true },
      suppressSizeToFit: false,
      width: 144,
      minWidth: 144,
    },
    domLayout: 'autoHeight',
  };

  const onSortChanged = (event: SortChangedEvent) => {
    if (event.source === 'uiColumnSorted') {
      event.columns?.forEach((sortedColumn) => {
        const fileColumnConfig = columnsDefinitions.find(
          (fileColumnConfig) => fileColumnConfig.colId == sortedColumn.getColId(),
        );
        if (fileColumnConfig) {
          fileColumnConfig.sort = sortedColumn.getSort();
        }
      });
      setColumnsDefinitions([...columnsDefinitions]);
    }
  };

  const columnsConfig = useMemo(() => {
    const columnsDefinitionsClone = [...columnsDefinitions];

    // All Files view: Show File Status but not Processing Status or Reason Code
    const allFilesColumnsConfig = columnsDefinitionsClone.filter(
      (col) => col.colId !== 'statusColumn' && col.colId !== 'reasonCodeColumn',
    );

    // Order Queue view: Show Processing Status and Reason Code but not File Status
    const queueColumnsConfig = columnsDefinitionsClone.filter((col) => col.colId !== 'fileInfoStatusColumn');

    return { allFilesColumnsConfig, queueColumnsConfig };
  }, [columnsDefinitions]);

  const bookOrder = async (): Promise<BookedOrderType> => {
    setIsPartialBook(selectedRows.length < queue.length);
    const token = await getAccessToken();
    return await bookOrderApi(
      {
        orderId: orderId,
        fileIds: selectedRows.map((row) => row.fileId),
        email: profile?.emailAddress ?? '',
      },
      token,
    );
  };

  const {
    execute: executeBookOrder,
    status: executeBookOrderStatus,
    value: bookedOrder,
    errors: bookOrderErrors,
  } = useAsync<BookedOrderType>(bookOrder, false);

  const queueFilesCount: FileCountsType = {
    allFiles: allFiles?.length || 0,
    orderQueue: queue?.length || 0,
  };
  const isOrderCancelled = getOrderFilesDetailsValue?.orderStatus === 'Cancelled';

  const applySearchPolicyNumberFilter = async () => {
    await gridApiRef.current?.setColumnFilterModel('policyNumbersSearchColumn', {
      filterType: 'text',
      type: 'contains',
      filter: searchPolicyNumbers,
    });
    gridApiRef.current?.onFilterChanged();
  };

  useEffect(() => {
    executeFileList();
    // Update the Order Status Label to the Header during the initial phase
    executeGetOrderFilesDetails();
  }, []);

  useEffect(() => {
    if (gridApiRef.current) {
      if (displayedRowCount === 0) {
        gridApiRef.current.showNoRowsOverlay();
      } else {
        gridApiRef.current.hideOverlay();
      }
    }
  }, [displayedRowCount]);

  useEffect(() => {
    if (executeBookOrderStatus === 'success') {
      setSessionData(bookedOrder);
      localStorage.removeItem('userFirm');
      let state = undefined;
      if (isPartialBook) {
        // Keep staying on the same page
        setOpenConfirmModal(false);
        executeGetOrderFilesDetails();
        orderData.targetTab = QueueFilter.OrderQueue;
        state = { order: orderData };
        history.replace('/order-management', state);
      } else if (location.state?.from) {
        history.replace(location.state.from, state);
      } else {
        history.replace('/order-management', state);
      }
    }
    if (executeBookOrderStatus === 'error') {
      // ANS-12793: a rejected booking comes back with the summary first and then one reason per
      // file. Stacked snackbars bury all but the last, so list them in a dialog instead and keep
      // the snackbar for the single-message case.
      if ((bookOrderErrors?.length ?? 0) > 1) {
        setOpenConfirmModal(false);
        setOpenBookOrderErrorModal(true);
      } else {
        bookOrderErrors?.map((err) => {
          addGlobalMsg({
            message: err,
            type: 'error',
          });
        });
      }
    }
  }, [executeBookOrderStatus]);

  useEffect(() => {
    if (enqueueFilesStatus === 'success') {
      // ANS-7025/ANS-13502: Check for file conflicts (files already associated with another open order)
      const fileConflicts = enqueueFilesValue?.result?.fileConflicts;
      if (fileConflicts && fileConflicts.length > 0) {
        // Warn and let the user confirm queuing anyway instead of blocking
        const conflictOrderIds = Array.from(new Set(fileConflicts.map((c) => c.existingOrderId)));
        setFileConflictOrderIds(conflictOrderIds);
        setOpenFileConflictModal(true);
        // Don't update UI yet - wait for the user to confirm or cancel
        return;
      }

      selectedRows.forEach((file) => {
        file.fileStatusId = ProcessingStatus.New;
        file.fileStatus = 'New';
        file.status = 'New';
        file.assignedToGroup = 'IFSC';
        file.assignedToGroupId = TeamAssignment.IFSC;
      });
      const remainingFilesAfterQueue = allFiles.filter((file) => !selectedRows.includes(file));
      const queueGuardUpdate = getQueueGuardUpdate(queue, selectedRows, closedFiles, 'enqueue');
      setAllFiles(remainingFilesAfterQueue);
      setSelectedFiles([]);
      setQueue(queueGuardUpdate.openFiles);
      getAllOrderAccountInfoApiExecute(queueGuardUpdate.accountNumbers);
      setDisplayedRowCount(queueFilesCount.orderQueue + selectedRows.length);
    }
    if (enqueueFilesStatus === 'error') {
      enqueueFilesErrors?.map((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [enqueueFilesStatus, enqueueFilesValue]);

  useEffect(() => {
    if (executeFileListStatus === 'success') {
      setAllFiles(fileListResults ?? []);
    }
    if (executeFileListStatus === 'error') {
      fileListErrors?.map((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [executeFileListStatus]);

  useEffect(() => {
    if (dequeueFilesStatus === 'success') {
      const queueGuardUpdate = getQueueGuardUpdate(queue, selectedRows, closedFiles, 'dequeue');
      setQueue(queueGuardUpdate.openFiles);
      getAllOrderAccountInfoApiExecute(queueGuardUpdate.accountNumbers);
      setSelectedQueueFiles([]);
      setAllFiles([...allFiles, ...selectedRows]);
      setDisplayedRowCount(queueFilesCount.allFiles + selectedRows.length);
    }
    if (dequeueFilesStatus === 'error') {
      dequeueFilesErrors?.map((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [dequeueFilesStatus]);

  useEffect(() => {
    if (executeGetOrderFilesDetailsStatus === 'success') {
      const openFiles = getOrderFilesDetailsValue?.openFiles ?? [];
      const closedOrderFiles = getOrderFilesDetailsValue?.closedFiles ?? [];
      setQueue(openFiles);
      setClosedFiles(closedOrderFiles);
      getAllOrderAccountInfoApiExecute(getOrderAccountNumbers(openFiles, closedOrderFiles));
      const accountNumbers = openFiles.map((file) => file.accountNumber);
      getAccountInfoApiExecute(extractUniqueAccountNumbers(accountNumbers));
      if (newFileCreated) {
        const newFile = getOrderFilesDetailsValue?.openFiles.find((f) => f.fileNumber === newFileCreated.fileName);
        if (newFile) {
          handleDrawerOpen({ data: newFile }, 'order-management', 1);
          setNewFileCreated(null);
        }
      }
    }
    if (executeGetOrderFilesDetailsStatus === 'error') {
      executeGetOrderFilesDetailsErrors?.map((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [executeGetOrderFilesDetailsStatus]);

  useEffect(() => {
    const combinedSelectedRows = selectedFiles
      .concat(queue)
      .concat(closedFiles.flatMap((closedFiled) => closedFiled.files));

    const grossTotal = calculateGrossTotal(combinedSelectedRows);
    const netTotal = calculateNetTotal(combinedSelectedRows);
    const netDifference = calculateNetDifference(netTotal, orderData?.reportedAmount || 0);

    setSelectedFilesAmount({ calculatedGross: grossTotal, calculatedNet: netTotal, netDifference: netDifference });
  }, [queue]);

  useEffect(() => {
    if (gridApiRef.current) {
      applySearchPolicyNumberFilter().catch(console.error);
    }
  }, [searchPolicyNumbers]);

  useEffect(() => {
    if (targetTab === QueueFilter.OrderQueue) {
      setQueueFilter(QueueFilter.OrderQueue);
      executeGetOrderFilesDetails();
    }
  }, [targetTab]);

  useEffect(() => {
    // Apply File Status filter when switching to All Files tab
    if (gridApiRef.current && allFilesTabSelected()) {
      const openStatusKey = getKeyByValue(FileInfoStatus, FileInfoStatus.Open);

      // First try to filter by "Open" status
      let filterValues: (string | null)[] = [openStatusKey];

      // If no "Open" status items exist, fall back to the first available status
      const hasOpenStatusFiles = allFiles.some((file) => file.fileInfoStatus === openStatusKey);

      if (!hasOpenStatusFiles && allFiles.length > 0) {
        // Get all unique file info statuses from the data, excluding null/undefined
        const availableStatuses = Array.from(
          new Set(
            allFiles
              .map((file) => file.fileInfoStatus)
              .filter((status): status is string => status !== null && status !== undefined),
          ),
        );

        // Use the first available status as fallback, or empty array if none exist
        filterValues = availableStatuses.length > 0 ? [availableStatuses[0]] : [];
      }

      gridApiRef.current.setFilterModel({
        fileInfoStatusColumn: {
          filterType: 'set',
          values: filterValues,
        },
      });
      gridApiRef.current.onFilterChanged();
    }
  }, [queueFilter, allFilesTabSelected(), allFiles]);

  const hasBookOrderActivityRight = profile?.activityRights?.some(
    (activityRight) => activityRight.ActivityRightId == ActivityRights.BookOrder,
  );
  const invalidRowSelected = selectedRows.some(
    (data) =>
      !data.isRemitValid ||
      moment(data.policyDate).toDate() > new Date() ||
      data.fileStatusId !== ProcessingStatus.Reconciled,
  );
  const hasSystemCancelledOrderAccount = hasSystemCancelledAccount(allOrderAccountInfoApiResult ?? []);
  const bookOrderButtonDisabled = isBookOrderDisabled({
    hasBookOrderActivityRight: Boolean(hasBookOrderActivityRight),
    invalidRowSelected,
    hasSystemCancelledOrderAccount,
    isAllOrderAccountInfoPending: allOrderAccountInfoApiStatus === AsyncStatus.PENDING,
  });

  const onNewFileCreated = async (file: CreateFileResponseModel): Promise<void> => {
    setNewFileCreated(file);
    const newFileIdString = file.fileId.toString();
    pendingConflictFileIdsRef.current = [newFileIdString];
    await executeEnqueueFiles([newFileIdString]);
    await executeGetOrderFilesDetails();
  };

  const onEditAssignmentSaved = useCallback(() => {
    gridApiRef.current?.deselectAll();
  }, []);

  if (!fileListResults) {
    return (
      <ContentContainer fullWidth>
        <Content>
          <CustomOverlayLoading />
        </Content>
      </ContentContainer>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.contentWrap}>
        <div className={classes.backButton}>
          <AgentNetButton
            color="primary"
            variant="text"
            size="medium"
            startIcon={<ArrowBack />}
            onClick={() => {
              localStorage.removeItem('userFirm');
              if (location.state?.from) {
                history.replace(location.state.from, undefined);
              } else {
                history.replace('/order-management', undefined);
              }
            }}
            data-testid={`${dataQaPrefix}-Back-To-All-Orders`}
          >
            Back to Dashboard
          </AgentNetButton>
        </div>
        <Box mx={3} my={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Box display="flex" alignItems="center">
              <Typography variant="h1" component="h1" className={classes.pageHeaderTitle}>
                Order ID: {orderId}
              </Typography>
              {getOrderFilesDetailsValue?.orderStatus && (
                <span
                  className={clsx(
                    classes.orderStatusLabel,
                    getOrderFilesDetailsValue.orderStatus === 'Open' ? classes.openStatus : classes.cancelledStatus,
                  )}
                  data-testid={`${dataQaPrefix}-Order-Status`}
                >
                  {getOrderFilesDetailsValue.orderStatus}
                </span>
              )}
            </Box>
            <Box>
              <Grid container>
                <Grid item style={{ margin: pxToRem(16, 0, 0, 0), display: 'flex-end' }}>
                  <AgentNetButton
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => {
                      const payload: GetNotesActionLog = {
                        orderId: orderId,
                        fileId: '0',
                      };
                      setIsDashboardMessagePanelOpen(true);
                      setConversationFileNumber('');
                      setConversationPayload(payload);
                    }}
                    data-testid="order-mgmt-select-files-button-order-notes"
                  >
                    <div className={classes.orderNotesIcon}>
                      <CellMessage />
                    </div>
                    <div className={classes.orderNotesLabel}>Order Notes</div>
                  </AgentNetButton>
                </Grid>
                <Grid item style={{ margin: pxToRem(16, 0, 16, 16), display: 'flex-end' }}>
                  <ButtonGroup>
                    {queueFilters.map((obj) => (
                      <AgentNetButton
                        key={obj?.value}
                        variant={queueFilter === obj?.value ? 'contained' : 'outlined'}
                        onClick={() => {
                          setQueueFilter(obj?.value);
                          setDisplayedRowCount(queueFilesCount[obj?.value as keyof FileCountsType]);
                        }}
                        data-testid={`${dataQaPrefix}-${[obj?.value]}`}
                        color="primary"
                        size="small"
                      >
                        {`${obj?.name} (${queueFilesCount[obj?.value as keyof FileCountsType]})`}
                      </AgentNetButton>
                    ))}
                  </ButtonGroup>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
        <Box marginX={3} marginTop={-3}>
          <Divider />
          <Grid container>
            <Grid item xs={6}>
              <PolicyNumberSearchField
                onSearchPolicy={(searchText: string) => {
                  setSearchPolicyNumbers(searchText);
                }}
              />
            </Grid>
            <Grid item xs={4} />
            <Grid item xs={2}>
              <div className={classes.createNewFileBtn}>
                <AgentNetButton
                  color="primary"
                  size="small"
                  variant="outlined"
                  data-testid={`${dataQaPrefix}-Create-New-File`}
                  disabled={isOrderCancelled}
                  hide={hideCreateNewFileButton}
                  onClick={() => setCreateNewFileDrawerOpen(true)}
                  plusIcon
                >
                  Create New File
                </AgentNetButton>
              </div>
            </Grid>
          </Grid>
        </Box>
        {orderQueueTabSelected() && closedFiles?.length ? (
          <ContentContainer fullWidth mTop="0">
            <Content className={classes.closedFilesContainer}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showClosedFiles ?? false}
                        onChange={() => {
                          setShowClosedFiles((prev) => !prev);
                        }}
                        name="Closed Files"
                        size="small"
                      />
                    }
                    label="Closed Files"
                    labelPlacement="start"
                    value={showClosedFiles ?? false}
                    className={classes.switch}
                    data-testid="order-mgmt-select-files-switch-closed-files"
                  />
                </Grid>
                {showClosedFiles &&
                  closedFiles.map((order) => (
                    <div key={order.orderNumber} className={classes.orderDetails}>
                      <Grid item xs={12} key={order.orderNumber}>
                        <Typography variant="h2" className={classes.orderNumber}>
                          Order Number: {order.orderNumber}
                        </Typography>
                        <DataTable {...closedFilesGridConfig} rowData={order.files} />
                        <Typography variant="body2" className={classes.fileCount}>
                          Total {order.files.length} files
                        </Typography>
                      </Grid>
                    </div>
                  ))}
              </Grid>
            </Content>
          </ContentContainer>
        ) : null}
        <div className={clsx('ag-theme-alpine', classes.tableDefault, classes.paddingX)}>
          <DataTable
            rowData={allFilesTabSelected() ? allFiles : queue}
            columnDefs={allFilesTabSelected() ? columnsConfig.allFilesColumnsConfig : columnsConfig.queueColumnsConfig}
            defaultColDef={defaultColDef}
            gridOptions={{ suppressRowClickSelection: true, rowSelection: 'multiple' }}
            onSelectionChanged={onSelectionChanged}
            onFilterChanged={onFilterChanged}
            animateRows={true}
            onSortChanged={onSortChanged}
            onGridReady={onGridReady}
            components={{
              customDropdownFilter: FloatingFilterComponent,
              customOverlayLoading: CustomOverlayLoading,
            }}
            loadingOverlayComponent={'customOverlayLoading'}
          />
        </div>
        <div className={clsx(classes.paddingX, classes.selectedRows)}>
          <span>
            Showing {displayedRowCount ?? getDisplayedRowCount()} of{' '}
            {queueFilesCount[queueFilter as keyof FileCountsType]} {'files'}
          </span>
          {selectedRows?.length ? selectedFilesText : null}
        </div>
        {selectedRows?.length ? (
          <BottomAppBar
            cancelLabel="Clear All"
            onCancel={() => {
              setSelectedRowsForTab([]);
              gridApiRef.current?.deselectAll();
            }}
            cancelDataQa="order-mgmt-select-files-button-clear-all-queue"
            {...(orderQueueTabSelected() && {
              primaryActionLabel: 'Book Order',
              onPrimaryClick: () => {
                setOpenConfirmModal(true);
              },
              primaryActionDataQa: 'order-mgmt-select-files-button-book-order-queue',
              disablePrimary: bookOrderButtonDisabled || isOrderCancelled,
              disabledPrimaryHoverText: hasSystemCancelledOrderAccount ? SYSTEM_CANCELLED_BOOK_TOOLTIP : '',
              omitPrimaryBorder: true,
            })}
            secondaryActionLabel={allFilesTabSelected() ? 'Queue' : 'Remove from Queue'}
            disableSecondary={disableSecondaryButton || isOrderCancelled}
            secondButtonLoader={disableSecondaryButton}
            onSecondaryClick={() => {
              if (allFilesTabSelected()) {
                const selectedFilesToQueue = allFiles.filter((file) => selectedRows.includes(file));
                const queueableFiles = selectedFilesToQueue.filter(isFileQueueable);
                const restrictedFiles = getRestrictedFiles(selectedFilesToQueue);

                if (restrictedFiles.length > 0 && queueableFiles.length > 0) {
                  addGlobalMsg({
                    message: `Cannot queue ${restrictedFiles.length} file(s) with restricted status. Only ${queueableFiles.length} file(s) will be queued.`,
                    type: 'warning',
                  });
                }

                if (queueableFiles.length > 0) {
                  const filteredFileIds: string[] = queueableFiles.map((file) => file.fileId);
                  pendingConflictFileIdsRef.current = filteredFileIds;
                  executeEnqueueFiles(filteredFileIds);
                } else if (restrictedFiles.length === selectedFilesToQueue.length) {
                  addGlobalMsg({
                    message: 'No files can be queued. All selected files have a restricted status.',
                    type: 'error',
                  });
                }
              } else {
                const selectedFilesToRemoveFromQueue = queue.filter((file) => selectedRows.includes(file));
                const filteredFileIds: string[] = selectedFilesToRemoveFromQueue.map((file) => file.fileId);
                executeDequeueFiles(filteredFileIds);
              }
            }}
            secondaryActionDataQa={
              allFilesTabSelected()
                ? 'order-mgmt-select-files-button-queue'
                : 'order-mgmt-select-files-button-remove-from-queue'
            }
            thirdActionLabel={!allFilesTabSelected() ? 'Modify Assignments' : ''}
            disableThird={disableSecondaryButton || isOrderCancelled}
            thirdButtonLoader={disableSecondaryButton}
            onThirdClick={() => {
              for (const selectedQueueFile of selectedQueueFiles) {
                selectedQueueFile.orderId = orderId;
                selectedQueueFile.fullName = selectedQueueFile.assignedTo && getFullName(selectedQueueFile);
              }
              setEditAssignmentData(selectedQueueFiles);
              setEditAssignmentDrawerOpen(true);
            }}
            thirdActionDataQa="order-mgmt-select-files-button-modify-assignments-queue"
          />
        ) : null}
        <ReportsAndPayDrawer
          openDrawer={openDrawer}
          setOpenDrawer={setOpenDrawer}
          drawerData={drawerData}
          setDrawerData={setDrawerData}
          getUpdatedData={refreshFilesInTabAfterDrawerClose}
          rateAndFeeExecute={rateAndFeeExecute}
        />
        {editAssignmentDrawerOpen && (
          <FileEditAssignmentContainer
            open={editAssignmentDrawerOpen}
            setOpen={setEditAssignmentDrawerOpen}
            data={editAssignmentData}
            onSaved={onEditAssignmentSaved}
          />
        )}
        {createNewFileDrawerOpen && (
          <CreateNewFileDrawer
            openDrawer={createNewFileDrawerOpen}
            setOpenDrawer={setCreateNewFileDrawerOpen}
            onNewFileCreated={onNewFileCreated}
          />
        )}
        <AgentNetConfirmationDialog
          disabled={executeBookOrderStatus === 'pending'}
          onConfirm={executeBookOrder}
          open={openConfirmModal}
          onDismissAction={() => {
            setOpenConfirmModal(false);
          }}
          dialogTitle="Book Order"
          dialogBtnContent="Yes, Continue"
          dialogText={`Book ${selectedRows?.length} file(s) for ${numberValueFormatter(
            selectedRows.reduce((acc, row) => acc + row.net, 0),
            true,
          )}. Do you want to continue?`}
          qaAttrPrefix={'${dataQaPrefix}-Book-Order-Confirmation-'}
        />
        <AgentNetConfirmationDialog
          disabled={enqueueFilesStatus === 'pending'}
          onConfirm={() => {
            executeEnqueueFiles(pendingConflictFileIdsRef.current, true);
            setOpenFileConflictModal(false);
          }}
          open={openFileConflictModal}
          onDismissAction={() => {
            setOpenFileConflictModal(false);
          }}
          dialogTitle="File Already Queued"
          dialogBtnContent="Yes, Continue"
          dialogText={`This file is already associated to another open order: ${fileConflictOrderIds.join(
            ', ',
          )}. Do you want to queue it to this order as well?`}
          qaAttrPrefix={'${dataQaPrefix}-File-Conflict-Confirmation-'}
        />
        <AgentNetConfirmationDialog
          open={openBookOrderErrorModal}
          onConfirm={() => setOpenBookOrderErrorModal(false)}
          onDismissAction={() => setOpenBookOrderErrorModal(false)}
          dialogTitle="Unable to Book Order"
          dialogTextHTML={<BookingErrorReasons reasons={bookOrderErrors ?? []} />}
          dialogBtnContent="Got it!"
          dialogActionBtnJustifyContent="flex-end"
          showSecondaryActionBtn={false}
          qaAttrPrefix="BookOrderError"
        />
        {conversationPayload && (
          <ConversationDrawer
            conversationPayload={conversationPayload}
            conversationFileNumber={conversationFileNumber}
            onClose={() => setConversationPayload(undefined)}
            isDashboardMessagePanelOpen={isDashboardMessagePanelOpen}
            readOnly={false}
          />
        )}
      </div>
    </div>
  );
};

export default SelectFiles;
